import type { AgentProgressEvent, AgentTimelineEntry } from '../AgentTimeline/types';

// A long agent run can emit hundreds of progress events. Rendering an accordion (each re-parsing
// markdown) per event freezes the tab, so cap the timeline to the most recent entries — the live
// "thinking" view shows the latest progress while retained DOM stays bounded.
const MAX_TIMELINE_ENTRIES = 100;

/**
 * Convert agent progress events to timeline entries (capped to the most recent entries)
 */
export const convertEventsToTimelineEntries = (events: AgentProgressEvent[]): AgentTimelineEntry[] => {
  // Sort events by timestamp (ascending - oldest first). Timestamps are ISO 8601 strings, so a
  // lexicographic compare orders them chronologically without allocating a Date per comparison.
  const sortedEvents = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  // No `status`: chat events are a flat log, not a checklist, so entries render with the plain dot.
  return sortedEvents.slice(-MAX_TIMELINE_ENTRIES).map((event) => ({
    id: event.event_id,
    title: event.title || event.event_type || 'Progress Update',
    content: event.message || undefined,
    timestamp: event.timestamp,
  }));
};

/**
 * Calculate total duration from events
 */
export const calculateEventsDuration = (events: AgentProgressEvent[]): number | null => {
  if (events.length === 0) return null;

  const sortedEvents = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  const firstTimestamp = sortedEvents[0].timestamp;
  const lastTimestamp = sortedEvents[sortedEvents.length - 1].timestamp;

  const durationMs = new Date(lastTimestamp).getTime() - new Date(firstTimestamp).getTime();
  const durationSeconds = durationMs / 1000;

  return Math.round(durationSeconds);
};

/**
 * Get dynamic thinking title based on state and duration
 */
export const getThinkingTitle = (
  events: AgentProgressEvent[],
  isThinking: boolean,
  responseTime?: number | null
): string => {
  if (isThinking) {
    const lastEvent = events[events.length - 1];
    const title = lastEvent?.title || 'Thinking';
    return title.endsWith('...') ? title : `${title}...`;
  }

  const duration = responseTime ? Math.round(responseTime / 1000) : calculateEventsDuration(events);
  return duration ? `Thought for ${duration} seconds` : 'Thoughts';
};
